import { IAnalyzeImageResponse, IImageInfo, IImageObject } from '@src/api/types'
import { ChartDataItem } from '@src/components/BarChartComponent/types'
import { FileSource, FileStatus } from '@src/components/FilePreview/types'
import { FileItem } from '@src/components/FilePreviewList/FileItem'
import { createColoredMarker, MarkerData, Point } from '@src/components/Map/MapComponent'
import { groupByType, prepareBarChartData } from '@src/helpers/chart'
import { lang } from '@src/lang'
import { AxiosError } from 'axios'
import { makeAutoObservable, observable } from 'mobx'
import { createContext } from 'react'

export const fieldToLabel: Record<string, string> = {
    volume_m3: lang.volume,
    mass_kg: lang.mass,
    area_px: lang.area,
}

export const fieldToMeasure: Record<string, string> = {
    volume_m3: lang.volumeMeasure,
    mass_kg: lang.massMeasure,
    area_px: lang.areaMeasure,
}

export class MainPageStore {
    mapPoints: Point[] = []
    mapMarkers: MarkerData[] = []

    areaData: ChartDataItem[] = []
    massData: ChartDataItem[] = []
    volumeData: ChartDataItem[] = []

    trashTypes = observable.map<string, number>()
    totalValues: Record<string, number> = { volume_m3: 0, mass_kg: 0, area_px: 0 }

    totalFiles: FileItem[] = []
    completedFiles: string[] = []
    filesToRequest: FileItem[] = []
    activeWorkers: number = 0

    unzipStatus: boolean = false

    get progress() {
        return { completed: this.completedFiles.length, total: this.totalFiles.length }
    }

    constructor() {
        makeAutoObservable(this)
    }

    addActiveWorker() {
        this.activeWorkers += 1
    }

    removeActiveWorker() {
        this.activeWorkers -= 1
    }

    setUnzipStatus(value: boolean) {
        this.unzipStatus = value
    }

    addTotalFiles(files: File[]) {
        const filesNames = this.totalFiles.map(file => file.name)
        const filesToAdd = files.filter(file => !filesNames.includes(file.name))
        this.totalFiles.push(...filesToAdd.map(file => new FileItem(file, FileSource.FILE)))
    }

    addTotalFilesByLink(files: IImageInfo[]) {
        const filesNames = this.totalFiles.map(file => file.name)
        const filesToAdd = files.filter(file => !filesNames.includes(file.name))
        this.totalFiles.push(...filesToAdd.map(file => new FileItem(file, FileSource.LINK)))
    }

    setAbortControllerToFile(name: string, controller: AbortController) {
        this.totalFiles.find(item => item.name === name)?.setAbortController(controller)
    }

    addCompetedFile(newFile: FileItem) {
        this.completedFiles.push(newFile.name)
    }

    addMapPoints(newPoints: Point[]) {
        this.mapPoints.push(...newPoints)
    }

    addMapMarkers(newMarkers: MarkerData[]) {
        this.mapMarkers.push(...newMarkers)
    }

    addAreaData(newData: ChartDataItem[]) {
        this.areaData = groupByType([...this.areaData, ...newData])
    }

    addVolumeData(newData: ChartDataItem[]) {
        this.volumeData = groupByType([...this.volumeData, ...newData])
    }

    addMassData(newData: ChartDataItem[]) {
        this.massData = groupByType([...this.massData, ...newData])
    }

    addTrashTypes(newData: IImageObject[]) {
        newData.forEach(obj => {
            const current = this.trashTypes.get(obj.class_name) ?? 0
            this.trashTypes.set(obj.class_name, current + 1)
        })
    }

    addFilesToRequest(files: FileItem[]) {
        this.filesToRequest.push(...files)
    }

    updateFileStatus = (fileName: string, status: FileStatus) => {
        const file = this.totalFiles.find(file => file.name === fileName)
        file?.setStatus(status)
    }

    updateFileResult(fileName: string, result: IAnalyzeImageResponse) {
        const file = this.totalFiles.find(file => file.name === fileName)
        file?.setResult(result)
    }

    updateFileError(fileName: string, error: AxiosError) {
        const file = this.totalFiles.find(file => file.name === fileName)
        file?.setError(error)
    }

    addTotalValues(newData: IImageObject[]) {
        const total = newData.reduce(
            (acc, curr) => {
                acc.volume_m3 += curr.volume_m3
                acc.mass_kg += curr.mass_kg
                acc.area_px += curr.area_px
                return acc
            },
            {
                volume_m3: 0,
                mass_kg: 0,
                area_px: 0,
            }
        )

        this.totalValues.volume_m3 += total.volume_m3
        this.totalValues.mass_kg += total.mass_kg
        this.totalValues.area_px += total.area_px
    }

    setStatistics = (data: IImageObject[]) => {
        this.addTrashTypes(data)
        this.addTotalValues(data)
    }

    setChartsData = (data: IImageObject[]) => {
        const { volumeData, areaData, massData } = prepareBarChartData(data)

        this.addAreaData(areaData)
        this.addMassData(massData)
        this.addVolumeData(volumeData)
    }

    setMapData = (data: IImageObject[]) => {
        const newPoints: Point[] = []
        const newMarkers: MarkerData[] = []

        data.forEach(point => {
            const [lng, lat] = point.coordinates
            const [image_x, image_y] = point.image_coords
            const intensity = point.mass_kg

            newPoints.push([lat, lng, intensity])
            newMarkers.push({
                lat,
                lng,
                info: point,
                icon: createColoredMarker(point.class_id),
            })
        })

        this.addMapPoints(newPoints)
        this.addMapMarkers(newMarkers)
    }
}

const store = new MainPageStore()
export const MainPageStoreContext = createContext<MainPageStore>(store)
